import {Role} from '@sollapay/enums';
import {Badge, InfoNotice, RadioCard, RadioGroup, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import type {RoleDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface RolePickerRadioGroupProps {
  roles: RoleDTO[];
  /** Selected role's id (not key) — matches `RadioGroupItem`'s own `value`. */
  value: string;
  onChange: (roleId: string) => void;
  error?: string;
}

/** Phase 1 ships system roles only, so there's a single flat list — no custom-roles grouping. */
export const RolePickerRadioGroup: FC<RolePickerRadioGroupProps> = ({
  roles,
  value,
  onChange,
  error
}) => {
  const {t} = useTranslation(['team', 'common']);

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Sibling of RadioGroup, not a child of it — a RadioGroup (base-ui primitive) is only
          meant to contain Radio descendants. */}
      <Typography size="xs" weight="semibold" color="tertiary">
        {t('inviteModal.roleGroups.system')}
      </Typography>
      <RadioGroup
        value={value}
        onValueChange={val => onChange(val as string)}
        aria-invalid={!!error || undefined}
        className="flex flex-col gap-3"
      >
        {roles.map(role => {
          const isMember = role.key === Role.FIRM_MEMBER;

          return (
            <RadioCard
              key={role.id}
              value={role.id}
              size="sm"
              isDescriptionIndented
              isSelected={role.id === value}
              title={t(`common:roles.${role.key}`)}
              description={t(`common:roleDescriptions.${role.key}`)}
              aside={
                <Badge variant={isMember ? 'brand-subtle' : 'outline'} size="sm">
                  {t(`common:roleScopes.${role.key}`, {
                    defaultValue: t('inviteModal.permissionCount', {count: role.permissions.length})
                  })}
                </Badge>
              }
              footer={
                role.id === value && isMember ? (
                  <InfoNotice size="xs">{t('inviteModal.perAccountNote')}</InfoNotice>
                ) : undefined
              }
              className="rounded-xl"
            />
          );
        })}
      </RadioGroup>
      {error && (
        <Typography size="xs" className="text-destructive">
          {error}
        </Typography>
      )}
    </div>
  );
};
